import React, { useState, useEffect } from 'react';
import { BiPlus, BiTrash, BiSave, BiX } from 'react-icons/bi';

import Bouton from '../../../UI/Bouton/Bouton';
import ChampDeTexte from '../../../UI/ChampDeTexte/ChampDeTexte';
import ListeDeroulante from '../../../UI/ListeDeroulante/ListeDeroulante';
import TaskTypeInput from '../../TypesTasks/TaskTypeInput';

import * as type_btn from '../../../../ressources/glossaires/glossaire_type_de_bouton';
import * as type_alerte from '../../../../ressources/glossaires/glossaire_type_alerte';
import { checkMot } from '../../../../ressources/functionJS/checkMot';
import { generateId } from '../../../../ressources/functionJS/generateId';
import S from './FormulaireTasks.module.scss';

const FormulaireTasks = ({
  taskInfo = {},
  isEditMode = false,
  ajouterAlerte,
  annuler,
  sauvegarder,
}) => {
  // Form state
  const [formData, setFormData] = useState({
    id: '',
    nom: '',
    description: '',
    arguments: [],
  });

  const [erreurs, setErreurs] = useState({});

  // Available argument types
  const typesArguments = [
    { value: 'sensors_list', label: 'Liste de capteurs ID' },
    { value: 'date_range', label: 'Plage de dates' },
    { value: 'frequency', label: 'Fréquence/Date' },
    { value: 'number', label: 'Nombre' },
    { value: 'text', label: 'Texte' },
    { value: 'boolean', label: 'Booléen' },
    { value: 'select', label: 'Sélection' },
    { value: 'multi_select', label: 'Sélection multiple' },
    { value: 'file', label: 'Fichier' },
    { value: 'json', label: 'JSON' },
  ];

  // Initialize form data
  useEffect(() => {
    if (isEditMode && taskInfo.id) {
      setFormData({
        id: taskInfo.id || '',
        nom: taskInfo.nom || '',
        description: taskInfo.description || '',
        arguments: taskInfo.arguments || [],
      });
    } else {
      setFormData({
        id: generateId(),
        nom: '',
        description: '',
        arguments: [],
      });
    }
  }, [taskInfo, isEditMode]);

  // Handle form changes
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field
    if (erreurs[field]) {
      setErreurs((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  // Handle argument changes
  const handleArgumentChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      arguments: prev.arguments.map((arg, i) =>
        i === index ? { ...arg, [field]: value } : arg
      ),
    }));
  };

  // Add new argument
  const ajouterArgument = () => {
    const nouvelArgument = {
      id: generateId(),
      position: formData.arguments.length,
      nom: '',
      type: 'text',
      obligatoire: true,
      description: '',
      valeurParDefaut: '',
      options: {},
    };

    setFormData((prev) => ({
      ...prev,
      arguments: [...prev.arguments, nouvelArgument],
    }));
  };

  // Remove argument
  const supprimerArgument = (index) => {
    setFormData((prev) => ({
      ...prev,
      arguments: prev.arguments
        .filter((_, i) => i !== index)
        .map((arg, i) => ({ ...arg, position: i })),
    }));
  };

  // Move argument position
  const deplacerArgument = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= formData.arguments.length) return;

    setFormData((prev) => {
      const newArguments = [...prev.arguments];
      [newArguments[index], newArguments[newIndex]] = [
        newArguments[newIndex],
        newArguments[index],
      ];

      // Update positions
      return {
        ...prev,
        arguments: newArguments.map((arg, i) => ({ ...arg, position: i })),
      };
    });
  };

  // Validate form
  const validerFormulaire = () => {
    const nouvellesErreurs = {};

    if (!formData.nom.trim()) {
      nouvellesErreurs.nom = 'Le nom de la fonction est obligatoire';
    }

    if (!formData.id.trim()) {
      nouvellesErreurs.id = "L'ID de la fonction est obligatoire";
    }

    // Validate arguments
    formData.arguments.forEach((arg, index) => {
      if (!arg.nom.trim()) {
        nouvellesErreurs[`argument_${index}_nom`] =
          "Le nom de l'argument est obligatoire";
      }
    });

    setErreurs(nouvellesErreurs);
    return Object.keys(nouvellesErreurs).length === 0;
  };

  // Handle save
  const handleSave = () => {
    if (!validerFormulaire()) {
      ajouterAlerte(
        type_alerte.erreur,
        'Erreur de validation',
        'Veuillez corriger les erreurs dans le formulaire'
      );
      return;
    }

    sauvegarder(formData);
    ajouterAlerte(
      type_alerte.success,
      'Succès',
      `Fonction ${isEditMode ? 'modifiée' : 'créée'} avec succès`
    );
  };

  return (
    <div className={S.formulaireContainer}>
      <div className={S.header}>
        <h3 className={S.titre}>
          {isEditMode ? 'Modifier la fonction' : 'Créer une nouvelle fonction'}
        </h3>
      </div>

      <div className={S.contenuFormulaire}>
        {/* Task basic info */}
        <div className={S.section}>
          <h4 className={S.sousTitre}>Informations de base</h4>

          <div className={S.champGroupe}>
            <label className={S.label}>Nom de la fonction *</label>
            <ChampDeTexte
              value={formData.nom}
              onChange={(value) => handleInputChange('nom', value)}
              placeholder="Nom de la fonction"
              erreur={erreurs.nom}
            />
          </div>

          <div className={S.champGroupe}>
            <label className={S.label}>ID de la fonction *</label>
            <ChampDeTexte
              value={formData.id}
              onChange={(value) => handleInputChange('id', value)}
              placeholder="ID unique de la fonction"
              erreur={erreurs.id}
              disabled={isEditMode}
            />
          </div>

          <div className={S.champGroupe}>
            <label className={S.label}>Description</label>
            <ChampDeTexte
              value={formData.description}
              onChange={(value) => handleInputChange('description', value)}
              placeholder="Description de la fonction"
              multiline={true}
              rows={3}
            />
          </div>
        </div>

        {/* Arguments section */}
        <div className={S.section}>
          <div className={S.argumentsHeader}>
            <h4 className={S.sousTitre}>Arguments de la fonction</h4>
            <Bouton
              type={type_btn.success}
              cliquer={ajouterArgument}
              taille="small"
            >
              <BiPlus size={16} />
              Ajouter un argument
            </Bouton>
          </div>

          {formData.arguments.length === 0 ? (
            <div className={S.aucunArgument}>
              <p>
                Aucun argument défini. Cliquez sur "Ajouter un argument" pour
                commencer.
              </p>
            </div>
          ) : (
            <div className={S.argumentsList}>
              {formData.arguments.map((argument, index) => (
                <div key={argument.id} className={S.argumentItem}>
                  <div className={S.argumentHeader}>
                    <span className={S.argumentPosition}>#{index + 1}</span>
                    <div className={S.argumentActions}>
                      <Bouton
                        type={type_btn.secondary}
                        taille="small"
                        cliquer={() => deplacerArgument(index, 'up')}
                        disabled={index === 0}
                      >
                        ↑
                      </Bouton>
                      <Bouton
                        type={type_btn.secondary}
                        taille="small"
                        cliquer={() => deplacerArgument(index, 'down')}
                        disabled={index === formData.arguments.length - 1}
                      >
                        ↓
                      </Bouton>
                      <Bouton
                        type={type_btn.danger}
                        taille="small"
                        cliquer={() => supprimerArgument(index)}
                      >
                        <BiTrash size={14} />
                      </Bouton>
                    </div>
                  </div>

                  <div className={S.argumentContent}>
                    <div className={S.argumentBasicInfo}>
                      <div className={S.champGroupe}>
                        <label className={S.label}>Nom de l'argument *</label>
                        <ChampDeTexte
                          value={argument.nom}
                          onChange={(value) =>
                            handleArgumentChange(index, 'nom', value)
                          }
                          placeholder="Nom de l'argument"
                          erreur={erreurs[`argument_${index}_nom`]}
                        />
                      </div>

                      <div className={S.champGroupe}>
                        <label className={S.label}>Type d'argument</label>
                        <ListeDeroulante
                          value={argument.type}
                          onChange={(value) =>
                            handleArgumentChange(index, 'type', value)
                          }
                          options={typesArguments}
                        />
                      </div>
                    </div>

                    <TaskTypeInput
                      type={argument.type}
                      argument={argument}
                      onChange={(field, value) =>
                        handleArgumentChange(index, field, value)
                      }
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className={S.actions}>
        <Bouton
          type={type_btn.secondary}
          cliquer={annuler}
          className={`${S.btnAction} ${S.btnCancel}`}
        >
          <BiX size={16} />
          Annuler
        </Bouton>
        <Bouton
          type={type_btn.success}
          cliquer={handleSave}
          className={`${S.btnAction} ${S.btnSave}`}
        >
          <BiSave size={16} />
          {isEditMode ? 'Modifier' : 'Créer'}
        </Bouton>
      </div>
    </div>
  );
};

export default FormulaireTasks;
